import { useEffect, useMemo, useRef, useState } from 'react';
import appIconDark from '@/assets/app-icon-splash-dark.png';
import appIconLight from '@/assets/app-icon-light.png';
import { Button } from '@/components/ui/button';

interface SplashScreenProps {
  isLoading: boolean;
  /** Minimum time the splash stays visible after `isLoading` flips to false. */
  minDisplayTime?: number;
  /** If loading takes too long, show Retry/Continue helpers (does not block the app). */
  maxLoadingTime?: number;
}

// Fixed timings for the animated sequence. Total ≈ 1.55s in the happy path.
const FADE_IN_MS = 450;
const HOLD_MS = 700;
const FADE_OUT_MS = 400;

/**
 * Full-screen splash overlay. Runs a single fade-in → hold → fade-out
 * sequence on the first mount of the session, then unmounts and never
 * shows again (guarded by `window.__splashShown`).
 *
 * The background reads the app's own `--pc-app-bg` token so it matches
 * both light and dark themes exactly, giving a seamless handoff from
 * the native iOS launch screen (which uses the same colours via
 * `SplashBackground` in the asset catalog).
 *
 * On native (Capacitor), the native launch screen is configured with
 * `launchAutoHide: false`. We call `SplashScreen.hide()` from here on
 * mount, so the native image only disappears once this overlay is
 * rendered on top of it — no blank frame, no visible switch.
 */
export function SplashScreen({
  isLoading,
  minDisplayTime = 0,
  maxLoadingTime = 8000,
}: SplashScreenProps) {
  const alreadyShown =
    typeof window !== 'undefined' && (window as any).__splashShown === true;

  const [mounted, setMounted] = useState(!alreadyShown);
  // Animation phases: 'in' (fade+scale up), 'hold', 'out' (fade whole overlay).
  const [phase, setPhase] = useState<'in' | 'hold' | 'out'>('in');
  const [showSlowMessage, setShowSlowMessage] = useState(false);
  const readyToExitRef = useRef(false);
  const holdDoneRef = useRef(false);

  const reduceMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  // Hide the native (Capacitor) splash as soon as this overlay mounts, so the
  // in-app splash covers the moment the native image disappears.
  useEffect(() => {
    if (!mounted) return;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { SplashScreen: NativeSplash } = await import('@capacitor/splash-screen');
        // Deliberately NOT gated on `cancelled`. With `launchAutoHide: false`
        // the native splash is only ever dismissed from here, so skipping the
        // call because this overlay unmounted mid-import would leave the app
        // stuck behind the launch image with no second chance (a later mount
        // is suppressed by the `__splashShown` guard). Hiding an already-hidden
        // splash is a harmless no-op, so always follow through.
        await NativeSplash.hide({ fadeOutDuration: 60 });
      } catch {
        // Non-native or plugin unavailable — nothing to hide.
      }
    })();
  }, [mounted]);

  const dismiss = useMemo(
    () => () => {
      if (typeof window !== 'undefined') {
        (window as any).__splashShown = true;
      }
      setPhase('out');
      window.setTimeout(() => setMounted(false), FADE_OUT_MS + 20);
    },
    [],
  );

  const tryExit = useMemo(
    () => () => {
      if (!holdDoneRef.current) return;
      if (isLoading) return;
      if (!readyToExitRef.current) return;
      dismiss();
    },
    [dismiss, isLoading],
  );

  // Drive the fade-in → hold sequence on mount. The sequence runs on plain
  // timers so it works identically on native and in the browser/preview
  // (nothing here awaits Capacitor). A hard-cap timer guarantees the overlay
  // always dismisses even if `isLoading` never flips to false.
  useEffect(() => {
    if (!mounted) return;

    // Hard safety: no matter what, remove the overlay after this window so a
    // stuck auth state (or a thrown error in the sequence) can never leave
    // the splash covering the app.
    const HARD_CAP_MS = FADE_IN_MS + HOLD_MS + 3000 + minDisplayTime;
    const hardCap = window.setTimeout(() => {
      try {
        dismiss();
      } catch {
        // Fail open — force unmount.
        if (typeof window !== 'undefined') (window as any).__splashShown = true;
        setMounted(false);
      }
    }, HARD_CAP_MS);

    if (reduceMotion) {
      setPhase('hold');
      holdDoneRef.current = true;
      const t = window.setTimeout(() => {
        readyToExitRef.current = true;
        tryExit();
      }, Math.max(minDisplayTime, 100));
      return () => {
        window.clearTimeout(t);
        window.clearTimeout(hardCap);
      };
    }

    const inTimer = window.setTimeout(() => {
      setPhase('hold');
    }, FADE_IN_MS);

    const holdTimer = window.setTimeout(() => {
      holdDoneRef.current = true;
      readyToExitRef.current = true;
      tryExit();
    }, FADE_IN_MS + HOLD_MS + minDisplayTime);

    return () => {
      window.clearTimeout(inTimer);
      window.clearTimeout(holdTimer);
      window.clearTimeout(hardCap);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, reduceMotion]);

  // Re-attempt exit whenever loading flips.
  useEffect(() => {
    tryExit();
  }, [isLoading, tryExit]);

  // Slow-loading helper: after maxLoadingTime, expose Retry/Continue.
  useEffect(() => {
    if (!isLoading) {
      setShowSlowMessage(false);
      return;
    }
    const t = window.setTimeout(() => setShowSlowMessage(true), maxLoadingTime);
    return () => window.clearTimeout(t);
  }, [isLoading, maxLoadingTime]);

  if (!mounted) return null;

  const overlayOpacity = phase === 'out' ? 0 : 1;
  const logoOpacity = phase === 'in' ? 0 : 1;
  const logoScale = phase === 'in' ? 0.96 : 1;

  // Themed hex fallback so the overlay can NEVER paint transparent, even if
  // index.css hasn't parsed yet. Light mode uses pure white so the white-canvas
  // logo blends seamlessly.
  const isDarkShell =
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark');
  const shellFallback = isDarkShell ? '#070809' : '#FFFFFF';

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center will-change-[opacity]"
      style={{
        backgroundColor: shellFallback,
        backgroundImage: isDarkShell ? 'var(--pc-app-bg)' : 'none',
        opacity: overlayOpacity,
        transition: `opacity ${FADE_OUT_MS}ms ease-out`,
        pointerEvents: phase === 'out' ? 'none' : 'auto',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
      }}
      aria-busy={isLoading}
      aria-hidden={phase === 'out'}
    >
      <div
        style={{
          opacity: logoOpacity,
          transform: `scale(${logoScale})`,
          transition: reduceMotion
            ? 'none'
            : `opacity ${FADE_IN_MS}ms ease-out, transform ${FADE_IN_MS}ms ease-out`,
          willChange: 'opacity, transform',
        }}
      >
        <img
          src={isDarkShell ? appIconDark : appIconLight}
          alt="My Pocket Coach app icon"
          className="w-28 h-28 object-contain"
          style={{ backgroundColor: isDarkShell ? '#000000' : '#FFFFFF', borderRadius: 26 }}
          draggable={false}
        />
      </div>


      {showSlowMessage && phase !== 'out' && (
        <div className="mt-8 w-full max-w-xs px-6 text-center animate-fade-in">
          <p className="text-sm text-muted-foreground">
            This is taking longer than expected. You can retry or continue.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={dismiss}>
              Continue
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
